/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import template from './campaign-edit-page.component.html';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {lineItemCommons} from '../line-item/line-item-commons';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {isIabCategoryEnabledForNetwork} from '../line-item/iab-category-network';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {LineItemReviewResource} from '../../components/api/resources/resource/line-item.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {compareById} from '../common/util/object-id-comparison';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-campaign-edit-page',
  template
})
export class CampaignEditPageComponent extends AdminResourceEditPageBase {
  @Input() lineItemView: any;

  perms: any = {};
  tiers: any[] = [];
  lir: any = null;
  iabTaxonomy: any[] = [];
  showIabCategoryOnNetwork = false;
  isSelfService = false;
  isLockedDown = false;
  lineItemTargeting: any = {data: false};
  creativeTargeting: any = {data: false};
  unrejectError = false;
  maxWidth = 100;
  maxHeight = 100;
  showingChart: string | null = null;
  currentBurnRate: number | null = null;
  currentEndDate: any = null;
  currentSmoothed = false;

  private liCommons: any;
  private redirected = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly $state: StateService,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(LineItemReviewResource) private readonly LineItemReview: any,
    @Inject(CreativeResource) private readonly Creative: any,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.isSelfService = LocalUserPermissions.isSelfService();
    this.showIabCategoryOnNetwork = isIabCategoryEnabledForNetwork(LocalNetworkProfile.getNetworkTag());
    this.iabTaxonomy = _.cloneDeep(IAB_TAXONOMY);
    this.liCommons = lineItemCommons(this, this.translate, this.appLocale, LocalNetworkProfile, LocalUserPermissions, this.adnTimeShifter, 'editingModel');
  }

  protected afterInitEditingModel(): void {
    if (!this.editingModel?.product) {
      this.redirectToLineItem();
      return;
    }

    this.editingModel.objectives = this.editingModel.objectives || {};
    this.editingModel.bidSpecification = this.editingModel.bidSpecification || {};

    if (this.editingModel.startDate) {
      this.editingModel.modStartDate = this.editingModel.startDate;
    }
    if (this.editingModel.endDate) {
      this.editingModel.modEndDate = this.editingModel.endDate;
    }

    this.editingModel.advertiser = _.get(this.lineItemView, 'advertiser') || this.editingModel.advertiser;
    this.liCommons.setUpTiers();

    if (!this.isNew && this.editingModel.id) {
      this.LineItemReview.get({id: this.editingModel.id}, (data: any) => {
        // null → {} flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferMarkForCheck(this.cdr, () => {
          this.lir = data;
        });
      }, () => {
        deferMarkForCheck(this.cdr, () => {
          this.lir = {};
        });
      });
    } else {
      // null → {} flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.lir = {};
      });
    }
  }

  protected onBeforeSubmit(): void {
    this.liCommons.modDates(this.editingModel.modStartDate, this.editingModel.modEndDate);
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  readonly compareById = compareById;

  unreject(creative: any): void {
    const creativeResource = new this.Creative({
      id: creative.id,
      userState: 'APPROVED',
      rejectedReason: null
    });

    const promises: any[] = [creativeResource.savePartial()];
    const rejCreatives = _.filter(this.lir?.creatives, (c: any) => c.creative.userState === 'REJECTED');
    if (rejCreatives.length === 1) {
      promises.push(new this.LineItem({id: this.editingModel.id, userState: 'APPROVED'}).savePartial());
    }

    Promise.all(promises).then(() => {
      this.ngZone.run(() => {
        this.editingModel.userState = 'APPROVED';
        creative.userState = 'APPROVED';
        creative.rejectedReason = null;
        this.unrejectError = false;
      });
    }).catch(() => {
      this.ngZone.run(() => {
        this.unrejectError = true;
      });
    });
  }

  editTarget(targeting: any, creative?: any): void {
    const targetingModel = creative ? new this.Creative(_.cloneDeep(creative)) : _.cloneDeep(this.editingModel);
    const typeTargeting = creative ? 'creativeTargeting' : 'lineItemTargeting';
    this[typeTargeting] = {data: false};

    this.targetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: targetingModel,
      pageType: 'campaign',
      reachEnabled: 'LINE_ITEM',
      targetingMode: null,
      overridableTargeting: undefined,
      mandatoryTargets: undefined,
      availableTargets: undefined,
      targeting,
      isLockedDown: this.isLockedDown
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      targetingModel.targeting = _.cloneDeep(targeting);
      if (creative) {
        creative.targeting = targetingModel.targeting;
      } else {
        this.editingModel.targeting = targetingModel.targeting;
      }
      if (!this.isNew) {
        targetingModel.savePartial('targeting').then((respLineItem: any) => {
          this.ngZone.run(() => {
            this[typeTargeting].data = true;
            _.set(targetingModel, 'validationWarnings', respLineItem.validationWarnings);
            if (!creative) {
              this.editingModel.validationWarnings = respLineItem.validationWarnings;
            }
          });
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  showStatistics(): void {
    this.showingChart = 'LINE_ITEM';
  }

  private redirectToLineItem(): void {
    if (this.redirected || !this.editingModel?.id) {
      return;
    }
    this.redirected = true;
    this.$state.go('app.adv.line-items.line-item', {id: this.editingModel.id});
  }
}
